import { Box, Typography } from "@mui/joy";
import { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";

import { buildEmptyMeasurements } from "../../../../constants/roofMeasurements";
import { useEstimateContractContextManager } from "../../hooks";
import { MeasurementRow, MeasurementTableHeader } from "./MeasurementRow";
import { VarianceSummary } from "./VarianceSummary";
import {
    buildMeasurementRows,
    buildVarianceSummary,
} from "./utils";

export function RoofMeasurements() {
    const { values, updateMeasurements, initLoading } =
        useEstimateContractContextManager();

    const methods = useForm({
        defaultValues: {
            Measurements: buildEmptyMeasurements(),
        },
    });

    function hydrateMeasurementsForm() {
        if (initLoading) return;
        if (Object.keys(values.measurements).length === 0) return;

        methods.reset({
            Measurements: {
                ...buildEmptyMeasurements(),
                ...values.measurements,
            },
        });
    }

    useEffect(() => {
        hydrateMeasurementsForm();
    }, [initLoading]);

    return (
        <FormProvider {...methods}>
            <RoofMeasurementsContent
                loading={initLoading}
                onMeasurementsChange={updateMeasurements}
            />
        </FormProvider>
    );
}

function RoofMeasurementsContent({
    loading = false,
    onMeasurementsChange,
}: {
    loading?: boolean;
    onMeasurementsChange: (measurements: any) => void;
}) {
    const { watch } = useFormContext();
    const onChangeRef = useRef(onMeasurementsChange);
    onChangeRef.current = onMeasurementsChange;

    useEffect(() => {
        const subscription = watch((formValues) => {
            onChangeRef.current(formValues.Measurements || {});
        });
        return () => subscription.unsubscribe();
    }, []);

    const measurements = watch("Measurements") || {};

    const rows = useMemo(
        () => buildMeasurementRows(measurements),
        [measurements],
    );
    const summary = useMemo(() => buildVarianceSummary(rows), [rows]);

    return (
        <Box mt={3}>
            <Box mb={2}>
                <Typography level="title-md">Roof Measurements</Typography>
                <Typography level="body-sm" color="neutral">
                    Review and edit actual measurements compared to calculated
                    values.
                </Typography>
            </Box>

            <Box
                sx={{
                    borderRadius: "4px",
                    border: "1px solid",
                    borderColor: "rgba(128, 128, 128, 0.2)",
                    bgcolor: "rgba(128, 128, 128, 0.05)",
                    backdropFilter: "blur(12px)",
                    overflow: "hidden",
                }}
            >
                <Box>
                    <VarianceSummary summary={summary} />
                </Box>

                <MeasurementTableHeader />
                {rows.map((row, index) => (
                    <MeasurementRow
                        key={row.key}
                        row={row}
                        loading={loading}
                        showDivider={index < rows.length - 1}
                    />
                ))}
            </Box>
        </Box>
    );
}
